import * as valibot from 'valibot';
import type { StorageLike } from './storage';
import { normalizeQuery } from './text';

/**
 * A remembered pick: an address or stop with coordinates. Optional
 * `stop_id` / `ext_id` / `ts` are preserved verbatim across save/load.
 */
export interface Recent {
  name: string;
  lat: number;
  lon: number;
  stop_id?: number;
  ext_id?: string;
  ts?: number;
}

/** A fetched suggestion; identical shape to {@link Recent} minus `ts`. */
export interface Suggestion {
  name: string;
  lat: number;
  lon: number;
  stop_id?: number;
  ext_id?: string;
  /** Marker set by coord-picked suggestions so recents re-hydrate as position, not address. */
  kind?: 'position';
}

/** Alias kept for callers imported before the shared {@link StorageLike} landed. */
export type RecentStorage = StorageLike;

/** Max entries kept per scope; MRU-ordered, oldest evicted first. */
export const RECENT_CAP = 8;

/** LocalStorage key prefix; scope is appended per input area (`route`, `dep`, `wk`). */
const RECENT_KEY_PREFIX = 'router.recents.v2.';

/** LocalStorage key for `scope`. */
export function recentKey(scope: string): string {
  return RECENT_KEY_PREFIX + scope;
}

/** Runtime schema mirroring {@link Recent}; required fields are validated, extras pass through. */
const RecentSchema = valibot.looseObject({
  name: valibot.pipe(valibot.string(), valibot.minLength(1)),
  lat: valibot.pipe(valibot.number(), valibot.finite()),
  lon: valibot.pipe(valibot.number(), valibot.finite()),
});

/** True when `item` is a well-formed {@link Recent}. */
export function isRecentRecord(item: unknown): item is Recent {
  return valibot.is(RecentSchema, item);
}

/**
 * Cache of the last parsed payload, per (storage instance, key). Repeat reads
 * of an unchanged payload skip the parse and the {@link isRecentRecord} filter —
 * meaningful on the per-keystroke autocomplete path where `mergeRecents` calls
 * `loadRecents` every stroke. Keyed by storage object identity so tests that
 * inject a distinct memory storage stay isolated from each other.
 */
const parsedCache = new WeakMap<RecentStorage, Map<string, { raw: string; parsed: Recent[] }>>();

/** Per-storage scope map lazily created on first access. */
function cacheFor(storage: RecentStorage): Map<string, { raw: string; parsed: Recent[] }> {
  let scopeCache = parsedCache.get(storage);
  if (!scopeCache) {
    scopeCache = new Map();
    parsedCache.set(storage, scopeCache);
  }
  return scopeCache;
}

/** Schema that accepts any array, letting each item be validated individually below. */
const UnknownArraySchema = valibot.array(valibot.unknown());

/** Parses `rawJson`, filters items through {@link isRecentRecord}, warns and returns [] on unreadable input. */
function parseRawPayload(rawJson: string): Recent[] {
  try {
    const parsed: unknown = JSON.parse(rawJson);
    if (!valibot.is(UnknownArraySchema, parsed)) return [];
    return parsed.filter(isRecentRecord);
  } catch (error: unknown) {
    console.warn('recents: unreadable payload, dropping', error);
    return [];
  }
}

/**
 * MRU list for `scope`; returns [] on unreadable JSON, non-array payloads, or
 * a missing storage entry. Warns to `console.warn` on parse failure. Errors
 * thrown by `storage.getItem` propagate (unlike the try/catch on the parse
 * path). Returns a defensive shallow copy — callers may sort / mutate freely
 * without corrupting the cache or subsequent reads.
 */
export function loadRecents(scope: string, storage: RecentStorage = localStorage): Recent[] {
  const storageKey = recentKey(scope);
  const rawJson = storage.getItem(storageKey);
  if (!rawJson) return [];
  const cache = cacheFor(storage);
  const cached = cache.get(storageKey);
  if (cached && cached.raw === rawJson) return [...cached.parsed];
  const list = parseRawPayload(rawJson);
  cache.set(storageKey, { raw: rawJson, parsed: list });
  return [...list];
}

/**
 * Stable identity string; used to dedup between recents and fetched results.
 * Rounds lat/lon to 5 decimals so µ-degree jitter collides.
 */
export function suggestionKey(suggestion: Suggestion): string {
  const latPart = Number.isFinite(suggestion.lat) ? suggestion.lat.toFixed(5) : '';
  const lonPart = Number.isFinite(suggestion.lon) ? suggestion.lon.toFixed(5) : '';
  return `${suggestion.name}|${latPart}|${lonPart}`;
}

/**
 * Adds `entry` to the front of `scope`'s MRU list, evicting the oldest past
 * {@link RECENT_CAP}. No-ops on invalid entries; swallows storage write failure
 * with a `console.warn`.
 */
export function rememberPick(
  scope: string,
  entry: unknown,
  storage: RecentStorage = localStorage,
): void {
  if (!isRecentRecord(entry)) return;
  const entryKey = suggestionKey(entry);
  const kept = loadRecents(scope, storage).filter((recent) => suggestionKey(recent) !== entryKey);
  kept.unshift({ ...entry, ts: Date.now() });
  try {
    storage.setItem(recentKey(scope), JSON.stringify(kept.slice(0, RECENT_CAP)));
  } catch (error: unknown) {
    console.warn('recents: write failed', error);
  }
}

/** Entries whose name contains `query` (trimmed, case-insensitive). */
export function filterRecents(list: Recent[], query: string): Recent[] {
  const normalized = normalizeQuery(query);
  if (!normalized) return list;
  return list.filter((recent) => recent.name.toLowerCase().includes(normalized));
}

/**
 * Filtered recents for `scope`, deduplicated against `fetched` by
 * {@link suggestionKey}, then concatenated (recents first). `fetched` may be
 * `undefined` — treated as an empty list.
 */
export function mergeRecents(
  scope: string,
  query: string,
  fetched: Suggestion[] | undefined,
  storage: RecentStorage = localStorage,
): Suggestion[] {
  const fetchedList = fetched ?? [];
  const seen = new Set(fetchedList.map((suggestion) => suggestionKey(suggestion)));
  const recents = filterRecents(loadRecents(scope, storage), query).filter(
    (recent) => !seen.has(suggestionKey(recent)),
  );
  return [...recents, ...fetchedList];
}
